import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    FormLabel,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import ReactQuill from "react-quill";
import { Close } from "@mui/icons-material";
import { ref } from "firebase/storage";
import { getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { firestore, storage } from "../../..";
import {
    deaneryList,
    parishListMap,
    type DeaneryOption as Deanery,
    type ParishOption as Parish,
} from "../../../utils/deanery_parishes";
import { useFeedback } from "../../../providers/feedback/feedback";
import { buildNewsSlug } from "../../../utils/news";
import "react-quill/dist/quill.snow.css";

function UploadNewsTab() {
    const [title, setTitle] = useState("");
    const [postedBy, setPostedBy] = useState("");
    const [subtitle, setSubTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
    const submitLockRef = useRef(false);
    const [parishList, setParishList] = useState<Parish[]>([]);
    const { showSnackbar } = useFeedback();
    const [_selectedDeanery, _setSelectedDeanery] = useState<{
        id: Number;
        name: string;
    } | null>(null);
    const [_selectedParish, _setSelectedParish] = useState<{
        id: Number;
        value: string;
    } | null>(null);

    const handleDeaneryChange = (
        event: React.SyntheticEvent<Element, Event>,
        value: Deanery | null
    ) => {
        if (value) {
            _setSelectedDeanery({ id: value.id, name: value.name });
            setParishList(parishListMap[value.name] || []);
            _setSelectedParish(null);
        } else {
            _setSelectedDeanery(null);
            setParishList([]);
        }
    };

    const handleReset = () => {
        setTitle("");
        setPostedBy("");
        setSubTitle("");
        setDescription("");
        setSelectedImages([]);
        setSelectedThumbnail(null);
        _setSelectedDeanery(null);
        _setSelectedParish(null);
        setParishList([]);
    };

    const handleUpload = async () => {
        if (isSubmitting) {
            return;
        }
        if (submitLockRef.current) {
            return;
        }

        if (!title.trim() || !postedBy.trim() || !subtitle.trim() || !description.trim() || !selectedThumbnail) {
            showSnackbar("Please fill all required fields");
            return;
        }

        try {
            submitLockRef.current = true;
            setIsSubmitting(true);
            const uploadKey = Date.now();

            const images = await Promise.all(
                selectedImages.map(async (image, index) => {
                    const storageRef = ref(storage, `news/${uploadKey}-${index}-${image.name}`);
                    const uploadTask = uploadBytesResumable(storageRef, image);
                    await uploadTask;
                    return getDownloadURL(storageRef);
                })
            );

            const storageRef = ref(storage, `news/${uploadKey}-thumbnail-${selectedThumbnail.name}`);
            const uploadTaskForThumbnail = uploadBytesResumable(
                storageRef,
                selectedThumbnail!
            );
            await uploadTaskForThumbnail;
            const downloadURL = await getDownloadURL(storageRef);

            const newsRef = await addDoc(collection(firestore, "news"), {
                thumbnail: downloadURL,
                title: title.trim(),
                subtitle: subtitle.trim(),
                gallery: images,
                description: description.trim(),
                deanerName: _selectedDeanery,
                selectedParishName: _selectedParish,
                id: new Date(),
                createdAt: new Date(),
                approved: true,
                postedBy: postedBy.trim(),
            });
            await updateDoc(doc(firestore, "news", newsRef.id), {
                slug: buildNewsSlug(title.trim(), newsRef.id),
            });

            showSnackbar("News uploaded successfully");
            handleReset();
        } catch (error) {
            console.error("Error uploading news: ", error);
            showSnackbar("Something went wrong");
        } finally {
            setIsSubmitting(false);
            submitLockRef.current = false;
        }
    };

    return (
        <Box mt={3}>
            <Box>
                <Box mt={3}>
                    <TextField
                        fullWidth
                        label="News posted by"
                        placeholder="Enter name"
                        value={postedBy}
                        onChange={(event) => {
                            setPostedBy(event?.target.value);
                        }}
                        required
                    />
                </Box>

                <Box sx={{ mt: 3 }}>
                    <TextField
                        fullWidth
                        label="Title of the news"
                        placeholder="Enter news title"
                        value={title}
                        onChange={(event) => {
                            setTitle(event?.target.value);
                        }}
                        required
                    />
                </Box>

                <Box mt={3}>
                    <TextField
                        fullWidth
                        rows={3}
                        multiline
                        label="Subtitle of the news"
                        placeholder="Enter news subtitle"
                        value={subtitle}
                        onChange={(event) => {
                            setSubTitle(event?.target.value);
                        }}
                        required
                    />
                </Box>

                <Box display={"flex"} mt={0} width={"100%"}>
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Thumbnail *</FormLabel>
                        <input
                            type="file"
                            accept="image/*"
                            style={{
                                border: `1px solid rgb(181, 176, 176)`,
                                width: "96%",
                                paddingLeft: 8,
                                paddingRight: 8,
                                paddingTop: 16,
                                paddingBottom: 16,
                                borderRadius: 5,
                                marginTop: 8,
                            }}
                            onChange={(e) => {
                                if (e.target.files != null) {
                                    const filesArray = e.target.files[0];
                                    setSelectedThumbnail(filesArray);
                                }
                            }}
                        />
                        {selectedThumbnail && (
                            <Box mt={2} position="relative" display="inline-block">
                                <img
                                    src={URL.createObjectURL(selectedThumbnail)}
                                    alt="Thumbnail Preview"
                                    style={{
                                        maxWidth: "200px",
                                        maxHeight: "150px",
                                        borderRadius: 5,
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedThumbnail(null)}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                        color: "#ffffff",
                                        backgroundColor: "rgba(0,0,0,0.5)",
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            </Box>
                        )}
                    </FormControl>
                </Box>

                <Box mt={2}>
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Description of the news *</FormLabel>
                        <ReactQuill
                            theme="snow"
                            value={description}
                            onChange={setDescription}
                            placeholder="Enter news description with formatting"
                            modules={{
                                toolbar: [
                                    ["bold", "italic", "underline", "strike"],
                                    ["blockquote", "code-block"],
                                    [{ header: 1 }, { header: 2 }],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    [{ align: [] }],
                                    ["link"],
                                    ["clean"],
                                ],
                            }}
                            formats={[
                                "bold",
                                "italic",
                                "underline",
                                "strike",
                                "blockquote",
                                "code-block",
                                "header",
                                "list",
                                "align",
                                "link",
                            ]}
                            style={{ height: "300px", marginTop: "10px" }}
                        />
                    </FormControl>
                </Box>

                <Box display={"flex"} width={"100%"} >
                    <FormControl fullWidth margin="normal">
                        <FormLabel>
                            <Typography style={{paddingTop: 24}}>Upload images (optional)</Typography>
                        </FormLabel>
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            style={{
                                border: `1px solid rgb(181, 176, 176)`,
                                width: "96%",
                                paddingLeft: 8,
                                paddingRight: 8,
                                paddingTop: 16,
                                paddingBottom: 16,
                                borderRadius: 5,
                                marginTop: 8,
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (e.target.files != null) {
                                    const filesArray = Array.from(e.target.files);
                                    setSelectedImages((prevImages: File[]) => [
                                        ...prevImages,
                                        ...filesArray,
                                    ]);
                                }
                            }}
                        />
                    </FormControl>
                </Box>

                <Box display={"flex"} flexDirection={"row"} flexWrap="wrap" mt={2}>
                    {selectedImages.length > 0 &&
                        selectedImages?.map((file: File, index: number) => {
                            return (
                                <Box
                                    key={index}
                                    display={"flex"}
                                    flexDirection={"row"}
                                    position={"relative"}
                                    mr={1}
                                    mb={1}
                                >
                                    <img
                                        src={URL.createObjectURL(selectedImages[index])}
                                        alt="Preview"
                                        style={{
                                            maxWidth: "100px",
                                            maxHeight: "80px",
                                            borderRadius: 5,
                                        }}
                                    />
                                    <Box
                                        display={"flex"}
                                        position={"absolute"}
                                        left={0}
                                        top={0}
                                    >
                                        <IconButton
                                            size={"small"}
                                            onClick={() => {
                                                let filter = selectedImages?.filter(
                                                    (i: File) => i !== file
                                                );
                                                setSelectedImages(filter);
                                            }}
                                            style={{
                                                color: "#ffffff",
                                                backgroundColor: "rgba(0,0,0,0.5)",
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Box>
                                </Box>
                            );
                        })}
                </Box>

                <Box mt={3}>
                    <Autocomplete
                        fullWidth
                        value={
                            deaneryList.find(
                                (option) => _selectedDeanery?.id === option.id
                            ) || null
                        }
                        onChange={handleDeaneryChange}
                        options={deaneryList}
                        getOptionLabel={(option) => option.name || ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Deanery Name (optional)"
                            />
                        )}
                    />
                </Box>

                <Box mt={3}>
                    <Autocomplete
                        fullWidth
                        value={
                            parishList.find(
                                (option: Parish) =>
                                    _selectedParish?.id === option.id
                            ) || null
                        }
                        onChange={(
                            e: React.SyntheticEvent,
                            value: Parish | null
                        ) => _setSelectedParish(value)}
                        options={parishList}
                        getOptionLabel={(option: Parish) => option.value || ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Parish name (optional)"
                            />
                        )}
                        disabled={!parishList.length}
                    />
                </Box>

                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    mt={4}
                    mb={2}
                >
                    <Button
                        onClick={handleReset}
                        variant="outlined"
                        sx={{
                            minWidth: 200,
                        }}
                    >
                        Clear Form
                    </Button>

                    <Button
                        onClick={() => {
                            handleUpload();
                        }}
                        sx={{
                            minWidth: 200,
                        }}
                        variant="contained"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Creating..." : "Create Post"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default UploadNewsTab;
